/**
 * Blowing a bubble and its shot (spec 02 §4 Shot), ported from rebb64 D_2301 L232A (making it) and
 * enemy-ai.s L0F48..L1090 (one step per pass, trapping, walls). Only the shot traps.
 */

import { FULL, GROW, JUST_BLOWN, LETTER, LIGHTNING, POPPING, SMALL, TRAPPED, WATER } from './codes';
import { addBubble, colOf, freeSlot, mapSolid, setCode } from './slots';
import {
  BUBBLE_LIFE, LEVEL_100, POTION_BELOW, SHOT_MAX_COL, SHOT_STEP_PX, SHOT_STEPS, SHOT_STEPS_LONG, TRAP_RANGE,
  TRAP_TIME,
} from './tuning';
import type { Bubble, Facing, Monster, PlayerId, World } from '../types';

/** The blower's candies (02 §7.5): purple 12 steps (and a halved grow index), blue two steps a pass. */
export interface Candy {
  purple: boolean;
  blue: boolean;
}

/**
 * L232A: a new shot at (x, y) - the blower's X (+8 facing right) and Y, VIC frame - in the highest
 * free slot. Its row and sub-row come from Y as the C64 takes them: (Y - $15) / 8 and (Y - $15) & 6.
 * Steps from $A77D ($88, or $8C with purple candy, which also sets $A77F); blue candy sets $A781.
 * Null with no slot free, or with Y above $15 (the C64 gives up there).
 */
export function shootBubble(
  world: World, owner: PlayerId | -1, x: number, y: number, dir: Facing, candy: Candy = { purple: false, blue: false },
): Bubble | null {
  const slot = freeSlot(world);
  const dy = y - 0x15;
  if (slot < 0 || dy < 0) return null;
  return addBubble(world, slot, {
    x, y, owner, content: { kind: 'empty' },
    motion: {
      code: JUST_BLOWN, row: dy >> 3, subY: dy & 6, steps: candy.purple ? SHOT_STEPS_LONG : SHOT_STEPS,
      timer: BUBBLE_LIFE, dir, credit: owner, halfGrow: candy.purple, fast: candy.blue,
    },
  });
}

/** L0F48: one pass of a shot - a step, and a second with blue candy while it is still a bubble. */
export function shotPass(world: World, b: Bubble): void {
  shotStep(world, b);
  const m = b.motion;
  if (m.code < LETTER && m.fast && m.steps !== 0) shotStep(world, b);
}

/**
 * L0F61..L0FFE: one step. The type follows the steps left (growing); X and the column move 8 px; a
 * monster within reach is trapped (the step ends there); else the wall cells in front are tested.
 */
function shotStep(world: World, b: Bubble): void {
  const m = b.motion;
  const before = m.steps;
  m.steps = (m.steps - 1) & 0xff;
  let grow = before & 0x7f;
  if (m.halfGrow) grow = Math.max(1, grow >> 1);
  let code = GROW[grow] ?? SMALL;
  let end = FULL;
  if (potionShot(world, b)) {
    m.timer++;
    if (code === FULL) code = LIGHTNING;
    end = LIGHTNING;
  }
  setCode(b, code);
  if (code === LIGHTNING) b.content = { kind: 'lightning' };
  const oldX = b.x;
  if (m.dir < 0) {
    b.x = (b.x - SHOT_STEP_PX) & 0xff;
    if (trapMonster(world, b)) return;
    // L0FBC: a lightning shot ($0A, >= 6) skips the wall test - the potion's bolts-to-be pass through.
    if (m.code >= WATER) return;
    if (wallLeft(world, b)) return wallStop(world, b, oldX, end);
  } else {
    if (colOf(b) === SHOT_MAX_COL) return wallStop(world, b, oldX, end);
    b.x = (b.x + SHOT_STEP_PX) & 0xff;
    if (trapMonster(world, b)) return;
    if (m.code >= WATER) return;
    if (wallRight(world, b)) return wallStop(world, b, oldX, end);
  }
  m.steps &= 0x7f;
  setCode(b, m.code);
}

/**
 * Level 100's lightning potion (D_7F53 turns D_0F73's JMP into a skip there): a shot whose timer is
 * below $0A - one blown by a potion holder, marked 8 + player by D_23DE - keeps its timer up a step
 * (the pass's count takes it down again) and becomes lightning ($0A) where it would grow full, the last
 * steps; a wall stops it as lightning. (With blue candy's two steps a pass the timer climbs past $0A
 * and the rest of the shot is plain - the C64's own quirk.)
 */
function potionShot(world: World, b: Bubble): boolean {
  return world.levelIndex === LEVEL_100 && b.motion.timer < POTION_BELOW;
}

/**
 * The wall cells a step tests (D_7BFE: map row = row - 4). Moving left, from the new column c: the
 * row above (only when the bubble reaches into it), the upper row at c + 1, the lower row at c.
 * Moving right: the row above at c + 1, the upper row at c, the lower row at c + 1. (So the bytes
 * $00/$29/$50 and $01/$28/$51 read; the mixed columns are the C64's.)
 */
function wallLeft(world: World, b: Bubble): boolean {
  const c = colOf(b);
  const r = b.motion.row - 4;
  return (b.motion.subY !== 0 && mapSolid(world, c, r)) || mapSolid(world, c + 1, r + 1) || mapSolid(world, c, r + 2);
}

function wallRight(world: World, b: Bubble): boolean {
  const c = colOf(b);
  const r = b.motion.row - 4;
  return (b.motion.subY !== 0 && mapSolid(world, c + 1, r)) || mapSolid(world, c, r + 1)
    || mapSolid(world, c + 1, r + 2);
}

/**
 * L100B: a wall. X goes back to the column it left, on the grid. On the first step the bubble bursts
 * at once, credited to player 2 if player 2 is within 16 px of it, else to player 1 (L101D..L1045);
 * otherwise the shot ends and it floats as a full bubble - or, a potion shot, as lightning (`end`: the
 * type the step left in $38).
 */
function wallStop(world: World, b: Bubble, oldX: number, end: number): void {
  const m = b.motion;
  b.x = ((((oldX - 0x14) & 0xff) & 0xf8) + 0x14) & 0xff;
  const first = (m.steps & 0x80) !== 0;
  m.steps = 0;
  if (end === LIGHTNING) b.content = { kind: 'lightning' };
  if (first) {
    const p2 = world.players[1];
    m.credit = Math.abs(p2.x - b.x) < 16 && Math.abs(p2.y - b.y) < 16 ? 1 : 0;
    m.saved = end;
    setCode(b, POPPING);
  } else {
    setCode(b, end);
  }
}

/** Monster sprite states 1..$0A (and the heart's $16+) are trappable (L105D): all but trapped or dead. */
export const trappable = (mon: Monster): boolean => mon.state !== 'trapped' && mon.state !== 'dead';

/**
 * L105B / L1090: the first monster (from the last sprite slot down) within |dx| < 16 and |dy| < 16
 * of the step is trapped: the shot ends, the escape timer starts at 160, the monster goes into the
 * bubble. True if one was.
 */
function trapMonster(world: World, b: Bubble): boolean {
  for (let i = world.monsters.length - 1; i >= 0; i--) {
    const mon = world.monsters[i];
    if (!mon || !trappable(mon)) continue;
    if (Math.abs(b.x - mon.x) >= TRAP_RANGE || Math.abs(b.y - mon.y) >= TRAP_RANGE) continue;
    const m = b.motion;
    m.steps = 0;
    m.timer = TRAP_TIME;
    m.flash = false;
    m.flashOff = false;
    b.content = { kind: 'monster', monsterId: mon.id };
    setCode(b, TRAPPED);
    mon.state = 'trapped';
    mon.bubbleId = b.id;
    world.events.push({ type: 'trapped', monsterId: mon.id, bubbleId: b.id });
    return true;
  }
  return false;
}
